import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Package, Plus, Trash2, Shield, DollarSign, Loader2, Command, FileText, Activity, Terminal, Tag, X, AlertTriangle, Ticket, Calendar, Check, Clock, Layers } from 'lucide-react';
import { User, Product, Transaction, Voucher, Category } from '../types';
import { 
    getAllUsers, getProducts, addProduct, deleteProduct, processDeposit, getTransactions, getVouchers, createVoucher, deleteVoucher, updateTransactionStatus,
    getCategories, createCategory, deleteCategory 
} from '../services/storage';

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'products' | 'logs' | 'vouchers'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  // Logs Filter
  const [logFilter, setLogFilter] = useState<'all' | 'pending'>('all');
  
  // Hierarchy Management State
  const [dbCategories, setDbCategories] = useState<Category[]>([]);
  const [selectedRoot, setSelectedRoot] = useState<'Games' | 'Apps'>('Games');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [catCreationStatus, setCatCreationStatus] = useState<'idle' | 'loading'>('idle');

  // New Product Form State
  const [newProdName, setNewProdName] = useState('');
  const [newProdPrice, setNewProdPrice] = useState('');
  // selectedProdCategory now holds the "Root:::Category" string from DB
  const [selectedProdCategory, setSelectedProdCategory] = useState<string>(''); 
  const [isProcessing, setIsProcessing] = useState(false);

  // Voucher Form State
  const [voucherName, setVoucherName] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherAmount, setVoucherAmount] = useState('');
  const [voucherExpiry, setVoucherExpiry] = useState('');
  const [voucherMsg, setVoucherMsg] = useState('');

  // Top Up State
  const [topUpAmounts, setTopUpAmounts] = useState<{[key: string]: string}>({});

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const [u, p, t, v, cats] = await Promise.all([
        getAllUsers(),
        getProducts(),
        getTransactions(),
        getVouchers(),
        getCategories()
      ]);
      setUsers(u || []); 
      setProducts(p || []);
      setTransactions(t || []);
      setVouchers(v || []);
      setDbCategories(cats || []);
    } catch (e) {
      console.error("AdminPanel: Error syncing databanks", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleTopUp = async (user: User) => {
    if (isProcessing) return;
    const amount = parseFloat(topUpAmounts[user.id] || '0');
    if (amount !== 0) {
      setIsProcessing(true);
      try {
        await processDeposit(user.id, amount);
        setTopUpAmounts({ ...topUpAmounts, [user.id]: '' });
        await refreshData();
      } catch (e) {
        console.error("TopUp Failed", e);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleMarkDone = async (txId: string) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
        await updateTransactionStatus(txId, 'completed');
        await refreshData();
    } catch (e) {
        console.error(e);
    } finally {
        setIsProcessing(false);
    }
  };

  // --- HIERARCHY FUNCTIONS ---

  const handleAddCategory = async () => {
      if (!newCategoryName || !selectedRoot) return;
      
      const fullPath = `${selectedRoot}:::${newCategoryName}`;
      console.log("Creating category path:", fullPath);

      setCatCreationStatus('loading');
      try {
          const res = await createCategory(fullPath);
          if (res.success) {
              setDbCategories(await getCategories());
              setNewCategoryName('');
          } else {
              alert("Error: " + res.message);
          }
      } catch (e) {
          alert("System Error");
      } finally {
          setCatCreationStatus('idle');
      }
  };

  const handleRemoveCategory = async (id: string) => {
      if (!confirm("Delete this category?")) return;
      await deleteCategory(id);
      setDbCategories(await getCategories());
  };

  // --- PRODUCT FUNCTIONS ---

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing) return;
    setErrorMsg(null);
    
    // selectedProdCategory is derived from dbCategories, which ensures it is 'Root:::Category'
    if (newProdName && newProdPrice && selectedProdCategory) {
      setIsProcessing(true);
      
      try {
        const generatedImage = `https://placehold.co/400x400/18181b/ff8c00/png?text=${encodeURIComponent(newProdName)}`;

        // CRITICAL FIX: Combine Root:::Category (selectedProdCategory) + Name
        // Result: Games:::Shooters:::Valorant
        const fullPath = `${selectedProdCategory}:::${newProdName}`;
        
        console.log("Submitting Product with Path:", fullPath);

        const result = await addProduct({
          name: newProdName,
          price: parseFloat(newProdPrice),
          image: generatedImage,
          category: selectedProdCategory, // Group (e.g. Games:::Shooters)
          category_path: fullPath,        // Full Path (Games:::Shooters:::Valorant)
          category_id: null,              // Explicitly null to avoid UUID errors
        });
        
        if ('error' in result) {
          setErrorMsg(`Failed: ${result.error?.message || 'Unknown DB Error'}`);
        } else {
          setNewProdName('');
          setNewProdPrice('');
          await refreshData();
        }
      } catch (e) {
        setErrorMsg("System error.");
      } finally {
        setIsProcessing(false);
      }
    } else {
        setErrorMsg("Please fill all fields.");
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (isProcessing) return;
    if (confirm('CONFIRM DELETION: Are you sure you want to remove this asset?')) {
      setIsProcessing(true);
      try {
        await deleteProduct(id);
        await refreshData();
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // --- VOUCHER FUNCTIONS ---

  const handleCreateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing) return;
    setVoucherMsg('');
    setIsProcessing(true);
    
    try {
        const payload = {
            voucher_name: voucherName,
            code: voucherCode.toUpperCase(),
            amount: parseFloat(voucherAmount),
            expires_at: voucherExpiry || new Date(Date.now() + 86400000 * 30).toISOString(),
        };

        const res = await createVoucher(payload);

        if (res.success) {
            setVoucherMsg('Voucher Generated Successfully!');
            setVoucherName('');
            setVoucherCode('');
            setVoucherAmount('');
            setVoucherExpiry('');
            await refreshData();
        } else {
            setVoucherMsg(`Error: ${res.message}`);
        }
    } catch (err: any) {
        setVoucherMsg(`System Error: ${err.message || 'Unknown error occurred'}`);
    } finally {
        setIsProcessing(false);
    }
  };

  const handleDeleteVoucher = async (id: string) => {
    if (isProcessing) return;
    if (confirm('Revoke this code immediately?')) {
        setIsProcessing(true);
        try {
            await deleteVoucher(id);
            await refreshData();
        } finally {
            setIsProcessing(false);
        }
    }
  };

  const totalRevenue = transactions
    .filter(t => t.type === 'purchase')
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  
  const studentCount = users.filter(u => u.role === 'student').length;

  // Filter categories for the Hierarchy Manager view based on selectedRoot state
  const displayedCategoriesInManager = dbCategories.filter(c => {
      return c.name.startsWith(`${selectedRoot}:::`);
  });

  return (
    <div className="max-w-7xl mx-auto font-mono text-sm">
      <div className="flex items-center gap-4 mb-8 border-b border-accent-500/30 pb-6">
        <div className="p-3 bg-zinc-900 rounded-none border border-accent-500 shadow-neon">
           <Terminal className="text-accent-500" size={24} />
        </div>
        <div className="flex-1">
           <h2 className="text-3xl font-bold text-white tracking-tight uppercase flex items-center gap-2">
             <span className="text-accent-500">Root</span> // puchiwallet_admin
           </h2>
           <p className="text-zinc-500 text-xs mt-1">SYSTEM_ADMINISTRATION_CONSOLE_V2.1</p>
        </div>
        {(isProcessing || isLoading) && <div className="text-accent-500 animate-pulse text-[10px]">SYNCING_DATABANKS...</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <div className="bg-black border border-accent-500/50 p-6 shadow-neon relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:opacity-40 transition-opacity">
            <Users size={64} className="text-accent-500" />
          </div>
          <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Total Registered Users</p>
          <p className="text-4xl font-bold text-white">{studentCount}</p>
        </div>
        <div className="bg-black border border-accent-500/50 p-6 shadow-neon relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:opacity-40 transition-opacity">
            <Activity size={64} className="text-accent-500" />
          </div>
          <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Total Platform Revenue</p>
          <p className="text-4xl font-bold text-accent-500">${(totalRevenue || 0).toFixed(2)}</p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-zinc-800 overflow-x-auto">
        {/* Navigation Tabs */}
        {[
            { id: 'users', label: 'User_DB', icon: Users },
            { id: 'products', label: 'Inventory', icon: Package },
            { id: 'vouchers', label: 'Vouchers', icon: Ticket },
            { id: 'logs', label: 'Tx_Logs', icon: FileText },
        ].map((tab) => (
            <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 border-t border-l border-r rounded-t-lg font-bold text-xs uppercase tracking-wider transition-all ${
                    activeTab === tab.id 
                    ? 'bg-accent-500 text-black border-accent-500' 
                    : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:text-white hover:bg-zinc-800'
                }`}
            >
                <tab.icon size={14} /> {tab.label}
            </button>
        ))}
      </div>

      <div className="bg-black border border-zinc-800 min-h-[400px] shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-accent-500/50 to-transparent"></div>
        
        {activeTab === 'users' && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-900/80 text-accent-500 text-xs uppercase font-bold tracking-wider border-b border-zinc-800">
                  <th className="p-4">User_ID</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Joined</th>
                  <th className="p-4 text-right">Balance</th>
                  <th className="p-4 text-right">Action (Use - for deduct)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-zinc-900/50 transition-colors group font-mono text-xs">
                    <td className="p-4 font-bold text-white group-hover:text-accent-500 transition-colors">
                      {user.username || 'Unnamed User'} 
                      {user.role === 'admin' && <span className="ml-2 text-[10px] bg-red-900/30 text-red-500 px-1 rounded border border-red-900">ADMIN</span>}
                    </td>
                    <td className="p-4 text-zinc-500">{user.email}</td>
                    <td className="p-4 text-zinc-600">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                    <td className="p-4 text-right font-bold text-white">${(user.balance || 0).toFixed(2)}</td>
                    <td className="p-4 flex justify-end">
                      <div className="flex items-center gap-0 border border-zinc-700 rounded overflow-hidden">
                        <div className="bg-zinc-900 px-2 py-1 border-r border-zinc-700">
                           <DollarSign size={12} className="text-zinc-500" />
                        </div>
                        <input
                          type="number"
                          placeholder="0.00"
                          className="w-20 bg-black py-1 px-2 text-white focus:outline-none text-right"
                          value={topUpAmounts[user.id] || ''}
                          onChange={(e) => setTopUpAmounts({...topUpAmounts, [user.id]: e.target.value})}
                        />
                        <button
                          onClick={() => handleTopUp(user)}
                          disabled={isProcessing}
                          className="bg-zinc-800 hover:bg-accent-500 hover:text-black text-zinc-300 px-3 py-1 transition-all disabled:opacity-50 border-l border-zinc-700 font-bold uppercase"
                        >
                          Modify
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-6">
                
                {/* CATEGORY & HIERARCHY MANAGER */}
                <div className="bg-zinc-900/20 p-6 border border-zinc-800">
                    <h3 className="text-accent-500 font-bold mb-4 uppercase tracking-wider flex items-center gap-2 border-b border-zinc-800 pb-2">
                        <Layers size={14}/> Hierarchy Manager
                    </h3>
                    
                    <div className="mb-4">
                        <p className="text-[10px] text-zinc-500 uppercase font-bold mb-2">1. Select Parent Root</p>
                        <select 
                            className="w-full bg-black border border-zinc-800 p-2 text-white text-xs focus:border-accent-500 focus:outline-none"
                            value={selectedRoot}
                            onChange={(e) => setSelectedRoot(e.target.value as 'Games' | 'Apps')}
                        >
                            <option value="Games">Games</option>
                            <option value="Apps">Apps</option>
                        </select>
                    </div>

                    <div className="mb-4">
                        <p className="text-[10px] text-zinc-500 uppercase font-bold mb-2">2. Manage Child Categories</p>
                        <div className="flex gap-2 mb-2">
                            <input 
                                type="text" 
                                className="flex-1 bg-black border border-zinc-800 p-2 text-white text-xs focus:border-accent-500 focus:outline-none"
                                placeholder={`New ${selectedRoot} Category...`}
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                            />
                            <button 
                                onClick={handleAddCategory}
                                disabled={catCreationStatus === 'loading'}
                                className="bg-zinc-800 hover:bg-accent-500 hover:text-black text-white px-3 py-2 border border-zinc-700 transition-colors"
                            >
                                {catCreationStatus === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {displayedCategoriesInManager.map(cat => {
                                const displayName = cat.name.split(':::')[1] || cat.name;
                                return (
                                    <span key={cat.id} className="bg-zinc-800 text-zinc-400 text-[10px] px-2 py-1 rounded border border-zinc-700 flex items-center gap-1 group">
                                        {displayName}
                                        <button onClick={() => handleRemoveCategory(cat.id)} className="hover:text-red-500"><X size={10} /></button>
                                    </span>
                                );
                            })}
                            {displayedCategoriesInManager.length === 0 && <span className="text-[10px] text-zinc-600 italic">No categories yet.</span>}
                        </div>
                    </div>
                </div>

                {/* ADD PRODUCT FORM */}
                <div className="bg-zinc-900/20 p-6 border border-zinc-800">
                    <h3 className="text-accent-500 font-bold mb-4 uppercase tracking-wider flex items-center gap-2 border-b border-zinc-800 pb-2">
                        <Plus size={14}/> Upload Asset
                    </h3>
                    {errorMsg && (
                      <div className="mb-4 p-2 bg-red-900/20 border border-red-900/50 rounded flex items-center gap-2 text-red-400 text-xs">
                         <AlertTriangle size={12} />
                         {errorMsg}
                      </div>
                    )}
                    <form onSubmit={handleAddProduct} className="space-y-4">
                        <div className="space-y-1">
                             <label className="text-[10px] text-zinc-500 uppercase font-bold">Asset Name</label>
                             <input
                                type="text"
                                required
                                className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none transition-colors"
                                value={newProdName}
                                onChange={(e) => setNewProdName(e.target.value)}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] text-zinc-500 uppercase font-bold">Category</label>
                            <select 
                                className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none transition-colors"
                                value={selectedProdCategory}
                                onChange={(e) => setSelectedProdCategory(e.target.value)}
                                required
                            >
                                <option value="" disabled>Select Root ::: Category</option>
                                {dbCategories.map(cat => (
                                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="space-y-1">
                            <label className="text-[10px] text-zinc-500 uppercase font-bold">Price ($)</label>
                            <input
                                type="number"
                                step="0.01"
                                required
                                className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none transition-colors"
                                value={newProdPrice}
                                onChange={(e) => setNewProdPrice(e.target.value)}
                            />
                        </div>

                        <button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full bg-accent-600 hover:bg-accent-500 text-black font-bold py-2 mt-2 uppercase tracking-wider transition-all disabled:opacity-50 shadow-neon"
                        >
                        {isProcessing ? 'INITIALIZING...' : 'UPLOAD ASSET'}
                        </button>
                    </form>
                </div>
            </div>

            {/* PRODUCT LIST */}
            <div className="lg:col-span-2 space-y-2 max-h-[600px] overflow-y-auto pr-2">
              <h3 className="text-zinc-500 text-xs font-bold uppercase mb-2">Inventory Manifest</h3>
              {products.map(product => {
                 // Helper parsing for display - Uses category_path first
                 const rawPath = product.category_path || product.category || '';
                 const displayRoot = rawPath.split(':::')[0] || 'Apps';
                 const displaySub = rawPath.split(':::')[1] || 'Uncategorized';
                 
                 return (
                 <div 
                    key={product.id}
                    className="flex items-center gap-4 bg-zinc-900/40 p-2 border border-zinc-800 hover:border-accent-500/50 transition-colors group"
                 >
                    <div className="w-12 h-12 bg-zinc-800 overflow-hidden border border-zinc-700 shrink-0">
                         <img src={product.image} alt={product.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                          <h4 className="font-bold text-white text-xs uppercase">{product.name}</h4>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border uppercase font-bold ${displayRoot === 'Games' ? 'bg-purple-900/30 text-purple-500 border-purple-900' : 'bg-blue-900/30 text-blue-500 border-blue-900'}`}>
                              {displayRoot}
                          </span>
                          <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded border border-zinc-700">{displaySub}</span>
                      </div>
                      <p className="text-accent-500 text-xs mt-1">${(product.price || 0).toFixed(2)}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      disabled={isProcessing}
                      className="text-zinc-600 hover:text-red-500 p-2 transition-colors disabled:opacity-50"
                      title="Remove Asset"
                    >
                      <Trash2 size={16} />
                    </button>
                 </div>
              )})}
              {products.length === 0 && <p className="text-zinc-600 text-center py-4">Database Empty.</p>}
            </div>
          </div>
        )}

        {/* VOUCHER TAB & LOGS TAB (Kept mostly same but ensuring they render) */}
        {activeTab === 'vouchers' && (
            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-zinc-900/20 p-6 border border-zinc-800">
                        <h3 className="text-accent-500 font-bold mb-4 uppercase tracking-wider flex items-center gap-2 border-b border-zinc-800 pb-2">
                            <Ticket size={14}/> Voucher Generator
                        </h3>
                         {voucherMsg && (
                            <div className={`mb-4 p-2 border rounded flex items-center gap-2 text-xs ${voucherMsg.includes('Success') ? 'bg-green-900/20 border-green-900 text-green-400' : 'bg-red-900/20 border-red-900 text-red-400'}`}>
                                {voucherMsg}
                            </div>
                         )}
                        <form onSubmit={handleCreateVoucher} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] text-zinc-500 uppercase font-bold">Campaign Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. New Year Gift"
                                    required
                                    className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none"
                                    value={voucherName}
                                    onChange={(e) => setVoucherName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-zinc-500 uppercase font-bold">Unique Code</label>
                                <input
                                    type="text"
                                    placeholder="e.g. NY2026"
                                    required
                                    className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none font-mono uppercase"
                                    value={voucherCode}
                                    onChange={(e) => setVoucherCode(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-zinc-500 uppercase font-bold">Value ($)</label>
                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    required
                                    className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none font-mono"
                                    value={voucherAmount}
                                    onChange={(e) => setVoucherAmount(e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] text-zinc-500 uppercase font-bold">Expiry Date</label>
                                <input
                                    type="date"
                                    required
                                    className="w-full bg-black border border-zinc-800 focus:border-accent-500 p-2 text-white focus:outline-none"
                                    value={voucherExpiry}
                                    onChange={(e) => setVoucherExpiry(e.target.value)}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isProcessing}
                                className="w-full bg-accent-600 hover:bg-accent-500 text-black font-bold py-2 mt-2 uppercase tracking-wider transition-all disabled:opacity-50 shadow-neon"
                            >
                                {isProcessing ? 'SAVING...' : 'CREATE VOUCHER'}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <h3 className="text-zinc-500 text-xs font-bold uppercase mb-4">Active Vouchers</h3>
                    <div className="max-h-[600px] overflow-y-auto space-y-2 pr-2">
                        {vouchers.map(v => {
                             const isExpired = new Date(v.expires_at) < new Date();
                             return (
                                <div key={v.id} className="flex justify-between items-center bg-zinc-900/40 p-4 border border-zinc-800 hover:border-accent-500/30 transition-all">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-mono font-bold text-accent-500 text-lg">{v.code}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded border uppercase ${
                                                v.is_used ? 'border-zinc-700 bg-zinc-800 text-zinc-500' : 
                                                isExpired ? 'border-red-900 bg-red-900/20 text-red-500' :
                                                'border-green-900 bg-green-900/20 text-green-500'
                                            }`}>
                                                {v.is_used ? 'USED' : isExpired ? 'EXPIRED' : 'ACTIVE'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-white font-bold">{v.voucher_name || 'Promo Code'} <span className="text-zinc-500 font-normal">(${(v.amount || 0).toFixed(2)})</span></p>
                                        <p className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
                                            <Calendar size={10} /> Expires: {new Date(v.expires_at).toLocaleDateString()}
                                            {v.is_used && ` • Used by: ${users.find(u => u.id === v.used_by)?.username || 'Unknown'}`}
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => handleDeleteVoucher(v.id)}
                                        className="text-zinc-600 hover:text-red-500 p-2"
                                        title="Revoke Code"
                                        disabled={isProcessing}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                             );
                        })}
                        {vouchers.length === 0 && <p className="text-zinc-600 text-center py-10 border border-dashed border-zinc-800 rounded">No active vouchers.</p>}
                    </div>
                </div>
            </div>
        )}

        {activeTab === 'logs' && (
           <div className="flex flex-col h-[600px]">
             <div className="flex items-center gap-2 p-2 bg-zinc-900/50 border-b border-zinc-800">
                <button
                    onClick={() => setLogFilter('all')}
                    className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${logFilter === 'all' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-white'}`}
                >
                    All Logs
                </button>
                <button
                    onClick={() => setLogFilter('pending')}
                    className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${logFilter === 'pending' ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30' : 'text-zinc-500 hover:text-orange-500'}`}
                >
                    <Clock size={12} /> Pending Orders
                </button>
             </div>

             <div className="overflow-x-auto flex-1">
                <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-black z-10 shadow-lg">
                    <tr className="text-zinc-500 text-xs uppercase font-bold tracking-wider border-b border-zinc-800">
                    <th className="p-4">Timestamp</th>
                    <th className="p-4">User</th>
                    <th className="p-4">Type</th>
                    <th className="p-4">Details</th>
                    <th className="p-4 text-right">Amount/Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-zinc-900">
                    {transactions
                      .filter(t => logFilter === 'all' || (logFilter === 'pending' && t.status === 'pending'))
                      .map(t => {
                        const user = users.find(u => u.id === t.user_id);
                        let badgeColor = '';
                        if (t.type === 'purchase') badgeColor = 'bg-red-900/20 text-red-500 border-red-900/30';
                        else if (t.type === 'topup') badgeColor = 'bg-green-900/20 text-green-500 border-green-900/30';
                        else if (t.type === 'deduction') badgeColor = 'bg-orange-900/20 text-orange-500 border-orange-900/30';
                        else if (t.type === 'voucher_redemption') badgeColor = 'bg-purple-900/20 text-purple-500 border-purple-900/30';
                        else badgeColor = 'bg-zinc-800 text-zinc-500';

                        const description = t.description || t.item_name || 'System Log';
                        const isPending = t.status === 'pending';

                        return (
                            <tr key={t.id} className="hover:bg-zinc-900/30 transition-colors font-mono text-xs">
                                <td className="p-4 text-zinc-500">{new Date(t.created_at).toLocaleString()}</td>
                                <td className="p-4 text-white">{user ? (user.username || 'Unknown') : 'Unknown'}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold border ${badgeColor}`}>
                                        {t.type}
                                    </span>
                                    {isPending && <span className="ml-2 px-2 py-1 bg-orange-500/20 text-orange-500 border border-orange-500/30 rounded text-[10px] uppercase font-bold">PENDING</span>}
                                </td>
                                <td className="p-4 text-zinc-400">
                                    {description}
                                    {t.verification_code && <div className="mt-1 text-accent-500 font-bold">{t.verification_code}</div>}
                                </td>
                                <td className={`p-4 text-right font-bold`}>
                                    {isPending ? (
                                        <button 
                                            onClick={() => handleMarkDone(t.id)}
                                            disabled={isProcessing}
                                            className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded uppercase text-[10px] flex items-center gap-1 ml-auto"
                                        >
                                            {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Mark Done
                                        </button>
                                    ) : (
                                        <span className={t.type === 'purchase' || t.type === 'deduction' ? 'text-red-500' : 'text-green-500'}>
                                            {t.type === 'purchase' || t.type === 'deduction' ? '-' : '+'}${(t.amount || 0).toFixed(2)}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;